<script setup lang="ts">
import { computed, ref } from 'vue';
import type { LatencySeries } from '@/types';
import { formatLatency, latencyTicks } from '@/utils/status';
import { statusLocale, statusMessages } from '@/i18n';

const props = defineProps<{
  series: LatencySeries;
}>();

/**
 * The chart is drawn in an abstract 1000x100 box and stretched to whatever
 * width the column has (`preserveAspectRatio="none"`). Every stroke carries
 * `vector-effect="non-scaling-stroke"`, so the stretch changes the geometry
 * without smearing the line into a wedge.
 *
 * Axis labels are HTML positioned over the box rather than SVG `<text>`,
 * because the same stretch would distort glyphs.
 */
const VIEW_W = 1000;
const VIEW_H = 100;

/** Round-hour spacing for the time axis. */
const X_TICK_HOURS = 6;

const timeFormatter = new Intl.DateTimeFormat(statusLocale, {
  hour: '2-digit',
  minute: '2-digit',
});

const startMs = computed(() => Date.parse(props.series.startsAt));
const stepMs = computed(() => props.series.stepSeconds * 1000);

/** Instant at the CENTRE of a bucket — the point the value actually describes. */
function bucketMs(index: number): number {
  return startMs.value + index * stepMs.value + stepMs.value / 2;
}

const measured = computed(() =>
  props.series.values
    .map((value, index) => ({ value, index }))
    .filter((point): point is { value: number; index: number } => point.value !== null),
);

const maxValue = computed(() =>
  measured.value.reduce((max, point) => Math.max(max, point.value), 0),
);

const ticks = computed(() => latencyTicks(maxValue.value));
const ceiling = computed(() => ticks.value[ticks.value.length - 1] ?? 1);

const latest = computed(() => measured.value[measured.value.length - 1]?.value ?? null);

function xOf(index: number): number {
  const slots = Math.max(1, props.series.values.length - 1);
  return (index / slots) * VIEW_W;
}

function yOf(value: number): number {
  return VIEW_H - (value / ceiling.value) * VIEW_H;
}

interface Point {
  x: number;
  y: number;
}

/**
 * Contiguous runs of measured buckets.
 *
 * A gap is drawn as a gap. Joining across it would invent a straight line
 * through minutes nothing was measured, and the invented segment is
 * indistinguishable from a real, calm one.
 */
const runs = computed<Point[][]>(() => {
  const result: Point[][] = [];
  let current: Point[] = [];

  props.series.values.forEach((value, index) => {
    if (value === null) {
      if (current.length > 1) result.push(current);
      current = [];
      return;
    }
    current.push({ x: xOf(index), y: yOf(value) });
  });
  if (current.length > 1) result.push(current);

  return result;
});

const segments = computed(() =>
  runs.value.map((run) => run.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ')),
);

/** Filled area under each run, closed down to the baseline. */
const areaPaths = computed(() =>
  runs.value.map((run) => {
    const first = run[0]!;
    const last = run[run.length - 1]!;
    const line = run.map((p) => `L ${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
    return `M ${first.x.toFixed(2)},${VIEW_H} ${line} L ${last.x.toFixed(2)},${VIEW_H} Z`;
  }),
);

/**
 * Time labels snapped to round hours.
 *
 * Spacing the labels evenly across the buckets instead would stamp them with
 * whatever minute each bucket happened to fall on — "10:57, 16:55, 22:54" —
 * which reads as noise. Round hours are placed at their true fractional
 * position, so the label still points at the moment it names.
 */
const xLabels = computed(() => {
  const firstMs = bucketMs(0);
  const lastMs = bucketMs(props.series.values.length - 1);
  const spanMs = lastMs - firstMs;
  if (spanMs <= 0) return [];

  const tickMs = X_TICK_HOURS * 60 * 60 * 1000;
  const labels: { at: number; label: string; left: number }[] = [];
  for (let at = Math.ceil(firstMs / tickMs) * tickMs; at <= lastMs; at += tickMs) {
    labels.push({
      at,
      label: timeFormatter.format(new Date(at)),
      left: ((at - firstMs) / spanMs) * 100,
    });
  }
  return labels;
});

const hoverIndex = ref<number | null>(null);

const hovered = computed(() => {
  if (hoverIndex.value === null) return null;
  const value = props.series.values[hoverIndex.value];
  if (value === undefined) return null;
  return {
    time: timeFormatter.format(new Date(bucketMs(hoverIndex.value))),
    value,
    /** Percent across the plot, for placing the marker and the readout. */
    left: (hoverIndex.value / Math.max(1, props.series.values.length - 1)) * 100,
  };
});

function onMove(event: MouseEvent): void {
  // Measured against the plot's own rect, not `offsetX`: inside the SVG the
  // event target is whichever <line>/<polyline>/<path> sits under the pointer,
  // and `offsetX` would then be relative to that shape's bounding box.
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  if (rect.width === 0) return;
  const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
  hoverIndex.value = Math.round(ratio * (props.series.values.length - 1));
}
</script>

<template>
  <figure class="latency">
    <figcaption class="latency-head">
      <span class="latency-title">{{ statusMessages.responseTimes }}</span>
      <span class="latency-current">{{ formatLatency(latest) }}</span>
    </figcaption>

    <div class="latency-body">
      <div
        class="latency-plot"
        role="img"
        :aria-label="
          statusMessages.responseTimesAria(formatLatency(latest), formatLatency(maxValue))
        "
        @mousemove="onMove"
        @mouseleave="hoverIndex = null"
      >
        <svg
          class="latency-svg"
          :viewBox="`0 0 ${VIEW_W} ${VIEW_H}`"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <line
            v-for="tick in ticks"
            :key="`grid-${tick}`"
            class="latency-grid"
            x1="0"
            :y1="yOf(tick)"
            :x2="VIEW_W"
            :y2="yOf(tick)"
            vector-effect="non-scaling-stroke"
          />
          <path
            v-for="(area, index) in areaPaths"
            :key="`area-${index}`"
            class="latency-area"
            :d="area"
          />
          <polyline
            v-for="(segment, index) in segments"
            :key="`seg-${index}`"
            class="latency-line"
            :points="segment"
            vector-effect="non-scaling-stroke"
          />
          <line
            v-if="hovered && hovered.value !== null"
            class="latency-cursor"
            :x1="xOf(hoverIndex ?? 0)"
            y1="0"
            :x2="xOf(hoverIndex ?? 0)"
            :y2="VIEW_H"
            vector-effect="non-scaling-stroke"
          />
        </svg>

        <div
          v-if="hovered"
          class="latency-readout"
          :style="{ left: `${hovered.left}%` }"
          aria-hidden="true"
        >
          {{ hovered.time }} · {{ formatLatency(hovered.value) }}
        </div>
      </div>

      <div class="latency-axis-y" aria-hidden="true">
        <span
          v-for="tick in ticks"
          :key="`label-${tick}`"
          class="latency-tick"
          :style="{ top: `${(1 - tick / ceiling) * 100}%` }"
        >
          {{ formatLatency(tick) }}
        </span>
      </div>
    </div>

    <div class="latency-axis-x" aria-hidden="true">
      <span
        v-for="tick in xLabels"
        :key="tick.at"
        class="latency-x-tick"
        :style="{ left: `${tick.left}%` }"
        >{{ tick.label }}</span
      >
    </div>
  </figure>
</template>

<style scoped>
.latency {
  margin: 0;
  min-width: 0;
}

.latency-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.latency-title {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--xt-fg-muted);
}

.latency-current {
  font-size: 0.7rem;
  color: var(--xt-fg-muted);
  font-variant-numeric: tabular-nums;
}

.latency-body {
  display: flex;
  align-items: stretch;
  gap: var(--xt-chart-axis-gap);
}

.latency-plot {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  height: 72px;
}

.latency-svg {
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.latency-grid {
  stroke: var(--xt-latency-grid);
  stroke-width: 1;
}

.latency-area {
  fill: var(--xt-latency-fill);
  stroke: none;
}

.latency-line {
  fill: none;
  stroke: var(--xt-latency-line);
  stroke-width: 1.5;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.latency-cursor {
  stroke: var(--xt-latency-axis);
  stroke-width: 1;
  stroke-dasharray: 2 2;
}

.latency-readout {
  position: absolute;
  top: -0.1rem;
  transform: translateX(-50%);
  padding: 0.1rem 0.35rem;
  border-radius: 0.2rem;
  background: var(--xt-bg-elevated);
  border: 1px solid var(--xt-border);
  color: var(--xt-fg-strong);
  font-size: 0.68rem;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  pointer-events: none;
}

.latency-axis-y {
  position: relative;
  flex: 0 0 auto;
  width: var(--xt-chart-axis-width);
}

.latency-tick {
  position: absolute;
  right: 0;
  transform: translateY(-50%);
  font-size: 0.65rem;
  color: var(--xt-latency-axis);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.latency-axis-x {
  position: relative;
  height: 0.95rem;
  margin-top: 0.2rem;
  margin-right: var(--xt-chart-gutter);
  font-size: 0.65rem;
  color: var(--xt-latency-axis);
  font-variant-numeric: tabular-nums;
}

.latency-x-tick {
  position: absolute;
  transform: translateX(-50%);
  white-space: nowrap;
}
</style>
